import { useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { Invoice } from "@common/types/invoice";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import type { PickerListRootProps } from "@design-system/Picker/PickerRoot.types";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { VStack } from "@design-system/VStack/VStack";
import { ScheduleApplyFormValues } from "./scheduleApplyFormHelpers";
import { getScheduleApplyTermStart } from "./scheduleApplyAccruedHelpers";
import {
    deriveInvoiceTermStart,
    getInvoiceScheduleAmount,
    getSchedulableInvoiceItemLines,
} from "./scheduleApplyInvoiceHelpers";
import { useScheduleApplySources } from "./useScheduleApplySources";
import styles from "./ScheduleApplyModal.module.scss";

const getInvoiceLabel = (invoice: Invoice) =>
    invoice.invoiceNumber ?? `Invoice ${invoice.id}`;

interface ScheduleApplyInvoiceFieldProps {
    container: PickerListRootProps<string>["container"];
    template: ScheduleTemplateResponse | undefined;
}

/**
 * Invoice picker of the Apply schedule form, searched on the server, plus the
 * optional line selection of the picked invoice. The picked invoice is kept
 * apart from the loaded rows, so a new search does not drop its label or lines.
 */
export const ScheduleApplyInvoiceField: React.FC<
    ScheduleApplyInvoiceFieldProps
> = ({ container, template }) => {
    const { values, setValues } = useFormikContext<ScheduleApplyFormValues>();
    const { invoices, invoicesStatus, invoicesLoadMore, setInvoiceSearch } =
        useScheduleApplySources({ entityId: values.entityId });
    const [pickedInvoice, setPickedInvoice] = useState<Invoice | null>(null);
    const selectedInvoice =
        pickedInvoice?.id === values.invoiceId
            ? pickedInvoice
            : invoices.find((invoice) => invoice.id === values.invoiceId);
    const invoiceItems = useMemo<Array<PickerItemData<string>>>(
        () =>
            invoices.map((invoice) => ({
                value: invoice.id,
                label: getInvoiceLabel(invoice),
            })),
        [invoices],
    );

    const selectInvoice = (invoiceId: string | null) => {
        const invoice = invoices.find((item) => item.id === invoiceId);
        const sourceDate = invoice ? deriveInvoiceTermStart(invoice) : "";
        setPickedInvoice(invoice ?? null);
        void setValues((current) => ({
            ...current,
            invoiceId,
            sourceLineIds: [],
            amount: invoice
                ? getInvoiceScheduleAmount({
                      lines: invoice.lines,
                      fallbackAmount: invoice.total,
                  })
                : 0,
            sourceDate,
            termStart: sourceDate
                ? getScheduleApplyTermStart({ template, sourceDate })
                : "",
        }));
    };

    const selectedLineIds = new Set(values.sourceLineIds);

    const itemLines = getSchedulableInvoiceItemLines(
        selectedInvoice?.lines ?? [],
    );

    const toggleInvoiceLine = (lineId: string) => {
        if (!selectedInvoice) {
            return;
        }
        void setValues((current) => {
            const nextLineIds = current.sourceLineIds.includes(lineId)
                ? current.sourceLineIds.filter((id) => id !== lineId)
                : [...current.sourceLineIds, lineId];
            return {
                ...current,
                sourceLineIds: nextLineIds,
                amount: getInvoiceScheduleAmount({
                    lines: selectedInvoice.lines,
                    sourceLineIds: nextLineIds,
                    fallbackAmount: selectedInvoice.total,
                }),
            };
        });
    };

    return (
        <>
            <VStack as="label" gap={6} className={styles.field}>
                <span className={styles.fieldLabel}>Invoice</span>
                <Picker.Select
                    items={invoiceItems}
                    searchable
                    filterable={false}
                    onInputValueChange={setInvoiceSearch}
                    status={invoicesStatus}
                    {...invoicesLoadMore}
                    searchPlaceholder="Search invoices..."
                    emptyLabel="No invoices found"
                    container={container}
                    value={values.invoiceId}
                    onValueChange={selectInvoice}
                    renderValue={
                        selectedInvoice
                            ? () => getInvoiceLabel(selectedInvoice)
                            : undefined
                    }
                    placeholder="Select invoice"
                    trigger={<Picker.Button variant="secondary" size="md" />}
                />
            </VStack>

            {itemLines.length > 1 && (
                <VStack gap={8} className={styles.lineSelection}>
                    <span className={styles.fieldLabel}>
                        Apply to specific lines (optional)
                    </span>
                    {itemLines.map((line) => (
                        <Checkbox
                            key={line.id}
                            checked={selectedLineIds.has(line.id)}
                            onCheckedChange={() => toggleInvoiceLine(line.id)}
                            label={
                                line.description ??
                                `Invoice line ${line.sourceId}`
                            }
                        />
                    ))}
                </VStack>
            )}
        </>
    );
};
